import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Fade
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import ScienceIcon from '@mui/icons-material/Science';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FactoryIcon from '@mui/icons-material/Factory';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import GroupsIcon from '@mui/icons-material/Groups';
import { usePermissions } from '../utils/permissions';
import { supabase } from '../supabaseClient';

const NavigationMenu = ({ company }) => {
  const navigate = useNavigate();
  const { canAccessPage, canViewEmployees, hasPermission, canViewImports, canViewExports, canViewExpenses, canViewAccounts, canViewCategories, canViewAccountingOverview, canViewAccountingBanking, canViewAccountingReceivables, canViewAccountingPayables, canViewAccountingForecast, canViewInventoryPage, canViewInventoryTransactions, canViewInventoryConfigurations } = usePermissions();

  const getFirstAccessibleManufacturingPath = () => {
    const manufacturingTabs = [
      { permission: 'production_overview', path: `/company/${company.id}/manufacturing/batching` },
      { permission: 'production_batching', path: `/company/${company.id}/manufacturing/batching` },
      { permission: 'production_batches', path: `/company/${company.id}/manufacturing/batching` },
      { permission: 'production_calendar', path: `/company/${company.id}/manufacturing/batching` },
      { permission: 'assembly', path: `/company/${company.id}/manufacturing/packaging` }
    ];

    for (const tab of manufacturingTabs) {
      if (canAccessPage(tab.permission)) {
        return tab.path;
      }
    }
    return null;
  };

  const getFirstAccessibleLineProductionPath = () => {
    const lineProductionTabs = [
      { 
        hasAccess: () => hasPermission('production.line.page.view', 'read'), 
        path: `/company/${company.id}/line-production` 
      },
      { 
        hasAccess: () => hasPermission('production.line.overview.view', 'read'), 
        path: `/company/${company.id}/line-production/overview` 
      },
      { 
        hasAccess: () => hasPermission('production.line.templates.view', 'read'), 
        path: `/company/${company.id}/line-production/templates` 
      }
    ];

    for (const tab of lineProductionTabs) {
      if (tab.hasAccess()) {
        return tab.path;
      }
    }
    return `/company/${company.id}/line-production`; // fallback
  };

  const getFirstAccessibleExpensesPath = () => {
    const expensesTabs = [
      { 
        hasAccess: () => canViewExpenses(), 
        path: `/company/${company.id}/expenses` 
      },
      { 
        hasAccess: () => canViewAccounts(), 
        path: `/company/${company.id}/expenses/accounts` 
      },
      { 
        hasAccess: () => canViewCategories(), 
        path: `/company/${company.id}/expenses/categories` 
      }
    ];

    for (const tab of expensesTabs) {
      if (tab.hasAccess()) {
        return tab.path;
      }
    }
    return `/company/${company.id}/expenses`; // fallback
  };

  const getFirstAccessiblePath = (sectionType) => {
    switch (sectionType) {
      case 'sales':
        if (canAccessPage('sales_overview')) return `/company/${company.id}/crm-overview`;
        if (hasPermission('sales.purchase_orders.page.view', 'read')) return `/company/${company.id}/proforma-invoices`;
        if (canAccessPage('customers')) return `/company/${company.id}/customers-list`;
        if (canAccessPage('invoices')) return `/company/${company.id}/invoices`;
        if (canAccessPage('price_lists')) return `/company/${company.id}/price-lists`;
        return `/company/${company.id}/crm-overview`;
        
      case 'products':
        if (canAccessPage('products')) return `/company/${company.id}/products`;
        if (canAccessPage('brands')) return `/company/${company.id}/brands`;
        return `/company/${company.id}/products`;
      
      case 'locations':
        if (hasPermission('locations.locations.view')) return `/company/${company.id}/locations`;
        if (hasPermission('locations.equipment_list.view')) return `/company/${company.id}/equipment-list`;
        if (hasPermission('locations.equipment_maintenance.view')) return `/company/${company.id}/equipment-maintenance`;
        return `/company/${company.id}/locations`;
        
      case 'inventory':
        if (canViewInventoryPage()) return `/company/${company.id}/inventory/inventory/inventory-list`;
        if (canViewInventoryTransactions()) return `/company/${company.id}/inventory/inventory/inventory-transactions`;
        if (canViewInventoryConfigurations()) return `/company/${company.id}/inventory/inventory/inventory-configurations`;
        if (canViewImports()) return `/company/${company.id}/inventory/imports`;
        if (canViewExports()) return `/company/${company.id}/inventory/exports`;
        return `/company/${company.id}/inventory/inventory/inventory-list`;

      case 'formulations':
        if (canAccessPage('formulations')) return `/company/${company.id}/formulations`;
        if (hasPermission('inventory.supplies.qc_tests.view') || hasPermission('inventory.raw_materials.qc_tests.view')) {
          return `/company/${company.id}/qc-tests`;
        }
        return `/company/${company.id}/formulations`;

      case 'manufacturing':
        if (canAccessPage('production_batches')) return `/company/${company.id}/manufacturing/batches`;
        if (canAccessPage('production_overview')) return `/company/${company.id}/manufacturing/overview`;
        if (canAccessPage('production_calendar')) return `/company/${company.id}/manufacturing/planning`;
        if (hasPermission('production.batch_requests.manufacturing_orders.view', 'read') || 
            hasPermission('production.batch_requests.maintenance_requests.view', 'read')) {
          return `/company/${company.id}/manufacturing/requests`;
        }
        return `/company/${company.id}/manufacturing/batches`;
        
      default:
        return null;
    }
  };

  const categories = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      color: '#4CAF50',
      path: `/company/${company.id}/dashboard`,
      permission: 'dashboard'
    },
    {
      title: 'Tasks',
      icon: <AssignmentIcon />,
      color: '#FF5722',
      path: `/company/${company.id}/tasks`
    },
    {
      title: 'Calendar',
      icon: <CalendarTodayIcon />,
      color: '#FF5722',
      path: `/company/${company.id}/calendar`,
      permission: 'calendar'
    },
    {
      title: 'Schedule',
      icon: <ScheduleIcon />,
      color: '#673AB7',
      path: `/company/${company.id}/schedule`,
      permission: 'schedule'
    },
    {
      title: 'Locations',
      icon: <LocationCityIcon />,
      color: '#3F51B5',
      path: getFirstAccessiblePath('locations'),
      hasAccess: () => {
        return hasPermission('locations.locations.view') || 
               hasPermission('locations.equipment_list.view') || 
               hasPermission('locations.equipment_maintenance.view');
      }
    },
    {
      title: 'Vendors',
      icon: <AccountBoxIcon />,
      color: '#9C27B0',
      path: (() => {
        if (hasPermission('supply_chain.vendor_purchase_orders.view')) {
          return `/company/${company.id}/purchase-orders`;
        }
        if (hasPermission('supply_chain.suppliers.view')) {
          return `/company/${company.id}/vendors`;
        }
        return `/company/${company.id}/purchase-orders`;
      })(),
      hasAccess: () => {
        return hasPermission('supply_chain.suppliers.view') || hasPermission('supply_chain.vendor_purchase_orders.view');
      }
    },
    {
      title: 'Expenses',
      icon: <ReceiptIcon />,
      color: '#C62828',
      path: getFirstAccessibleExpensesPath(),
      hasAccess: () => {
        return canViewExpenses() || canViewAccounts() || canViewCategories();
      }
    },
    {
      title: 'Accounting',
      icon: <MonetizationOnIcon />,
      color: '#1B5E20',
      path: (() => {
        if (canViewAccountingOverview()) return `/company/${company.id}/accounting/overview`;
        if (canViewAccountingBanking()) return `/company/${company.id}/accounting/banking`;
        if (canViewAccountingReceivables()) return `/company/${company.id}/accounting/receivables`;
        if (canViewAccountingPayables()) return `/company/${company.id}/accounting/payables`;
        if (canViewAccountingForecast()) return `/company/${company.id}/accounting/forecast`;
        return `/company/${company.id}/accounting/overview`;
      })(),
      hasAccess: () => {
        return canViewAccountingOverview() || 
               canViewAccountingBanking() || 
               canViewAccountingReceivables() || 
               canViewAccountingPayables() || 
               canViewAccountingForecast();
      }
    },
    {
      title: 'CRM',
      icon: <GroupsIcon />,
      color: '#2196F3',
      path: getFirstAccessiblePath('sales'),
      hasAccess: () => {
        return canAccessPage('sales_overview') || 
               hasPermission('sales.purchase_orders.page.view', 'read') ||
               canAccessPage('customers') || 
               canAccessPage('invoices') || 
               canAccessPage('price_lists');
      }
    },
    {  
      title: 'Products',
      icon: <LocalMallIcon />,
      color: '#FF9800',
      path: getFirstAccessiblePath('products'),
      hasAccess: () => {
        return canAccessPage('brands') || 
               canAccessPage('products');
      }
    },
    {
      title: 'Inventory',
      icon: <InventoryIcon />,
      color: '#009688',
      path: getFirstAccessiblePath('inventory'),
      hasAccess: () => {
        return canViewInventoryPage() ||
               canViewInventoryTransactions() ||
               canViewInventoryConfigurations() ||
               canViewImports() ||
               canViewExports();
      }
    },
    {
      title: 'QC / Formulations',
      icon: <ScienceIcon />,
      color: '#E91E63',
      path: getFirstAccessiblePath('formulations'),
      hasAccess: () => {
        return canAccessPage('formulations') ||
               hasPermission('inventory.supplies.qc_tests.view') ||
               hasPermission('inventory.raw_materials.qc_tests.view');
      }
    },
    {
      title: 'Batch Production',
      icon: <LocalDrinkIcon />,
      color: '#00BCD4',
      path: getFirstAccessiblePath('manufacturing'),
      hasAccess: () => {
        return canAccessPage('production_overview') || 
               canAccessPage('production_batches') ||
               canAccessPage('production_calendar') ||
               hasPermission('production.batch_requests.manufacturing_orders.view', 'read') ||
               hasPermission('production.batch_requests.maintenance_requests.view', 'read');
      }
    },
    {
      title: 'Line Production',
      icon: <FactoryIcon />,
      color: '#FF6F00',
      path: getFirstAccessibleLineProductionPath(),
      hasAccess: () => {
        return hasPermission('production.line.overview.view', 'read') || 
               hasPermission('production.line.page.view', 'read') || 
               hasPermission('production.line.templates.view', 'read');
      }
    },
    {
      title: 'Employees',
      icon: <PeopleIcon />,
      color: '#FFC107',
      path: `/company/${company.id}/employees`,
      permission: 'employees'
    },
    {
      title: 'Settings',
      icon: <SettingsIcon />,
      color: '#0277BD',
      path: `/company/${company.id}/settings`
    }
  ];

  const filteredCategories = categories.filter(category => {
    if (category.hasAccess) {
      return category.hasAccess();
    }
    if (category.permission) {
      return canAccessPage(category.permission);
    }
    return true;
  });

  const handleCategoryMouseDown = (path, event) => {
    if (event.button === 1) {
      window.open(path, '_blank', 'noopener');
      return;
    }

    if (event.button === 0) {
      navigate(path);
    }
  };

  return (
    <Fade in timeout={800} easing="cubic-bezier(0.4, 0, 0.2, 1)">
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 40px',
            background: '#ffffff',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              lineHeight: 1.2,
              color: '#1e1e1e',
              fontSize: '1.5rem',
              letterSpacing: '1px',
              cursor: 'default',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            GlowWorks
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover .signout-icon': {
                opacity: 1,
                transform: 'translateX(0px) scaleX(-1)'
              }
            }}
          >
            <LogoutIcon
              className="signout-icon"
              onClick={() => {
                supabase.auth.signOut();
                navigate('/');
              }}
              sx={{
                color: '#1e1e1e',
                fontSize: '1.2rem',
                opacity: 0,
                transform: 'translateX(-10px) scaleX(-1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                lineHeight: 1.2,
                color: '#1e1e1e',
                fontSize: '1.5rem',
                letterSpacing: '1px',
                cursor: 'default'
              }}
            >
              {company.name}
            </Typography>
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 }
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              justifyContent: 'center',
              justifyItems: 'center',
              mx: 'auto',
              gridTemplateColumns: {
                xs: 'repeat(2, 120px)',
                sm: 'repeat(3, 120px)',
                md: 'repeat(4, 120px)'
              },
              columnGap: '90px',
              rowGap: { xs: 3, sm: 3.5, md: 4 }
            }}
          >
            {filteredCategories.map((category) => (
              <Box
                key={category.title}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover .category-card': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 36px rgba(0, 0, 0, 0.16)',
                    borderColor: 'rgba(0, 0, 0, 0.18)',
                  },
                  '&:hover .category-icon': {
                    transform: 'scale(1.08)',
                  }
                }}
                onMouseDown={(event) => handleCategoryMouseDown(category.path, event)}
              >
                <Card
                  className="category-card"
                  sx={{
                    width: 120,
                    height: 120,
                    background: '#ffffff',
                    border: 'none',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.10)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    className="category-icon"
                    sx={{
                      color: category.color,
                      transition: 'transform 0.25s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': { fontSize: '2.2rem' }
                    }}
                  >
                    {category.icon}
                  </Box>
                </Card>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.72rem',
                    lineHeight: 1.3,
                    letterSpacing: 0.2,
                    textAlign: 'center',
                  }}
                >
                  {category.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default NavigationMenu;